import React, { useState, useEffect } from "react";
import LoginLayout from "./component/LoginLayout";
import { Spin, Button } from "antd";
// components page
import Login from "./component/Login";
import SignUp from "./component/SignUp";
import MultipleOffice from "./component/MultipleOffice";
import CreatePractice from "./component/CreatePractice";
import CreateOfficePractice from "./component/CreateOfficePractice";
import UpdatePractice from "./component/UpdatePractice";
import UpdateOfficePractice from "./component/UpdateOfficePractice";
import CreateOffice from "./component/CreateOffice";
import UpdateOffice from "./component/UpdateOffice";
import CreateBankAccount from "./component/CreateBankAccount";
import EnableBank from "./component/EnableBank";
import DisplayBankAccount from "./component/DisplayBankAccount";
import InviteUsers from "./component/InviteUsers";
import PortalCredentials from "./component/PortalCredentials";
import ProcedureCodeDefaults from "./component/ProcedureCodeDefaults";
import Purchase from "./component/Purchase";
import AddNewOffice from "./component/AddNewOffice";
import SikkaSetup from "./component/SikkaSetup";
import ForgetPass from "./ForgetPass";
import { controller } from "./controller";
import { afterBankAction } from "./lib/onboardingPlan";

const LoginPage = () => {
  const [mood, setMood] = useState("loading");
  const [practiceId, setPracticeId] = useState("-1");
  const [officeId, setOfficeId] = useState("-1");
  // The steps this registrant was given (null = unknown → today's behaviour).
  const [plan, setPlan] = useState(null);
  // moods :
  /*
        0. loading
        1. login
        2. sign up
        3. multipleOffice
        4. createPractice - createOfficePractice
        5. UpdatePractice - UpdateOfficePractice
        6. CreateOffice
        7. UpdateOffice
        8. configureBanking
        9. enableBank
        10. displayBankAccount
        11. InviteUsers
        12. Purchase
    */
  const goToSignUpPage = () => {
    setMood("signUp");
  };

  const goToLoginPage = () => {
    setMood("login");
  };

  const readOnboardingStatus = async () => {
    // A NOT-yet-signed-in invitee arriving via an emailed ?token= link → the
    // (invite-gated) signup form, which prefills the code from the URL. The
    // `!user` guard is essential: the invite link's ?token= stays in the URL for
    // the whole hash-routed session, so once the invitee has registered, verified,
    // and logged in, this branch would otherwise keep bouncing them back to the
    // signup form instead of into onboarding.
    if (window.location.href.includes("?token=") && !localStorage.getItem("user")) {
      setMood("signUp");
    } else {
      // check user is login
      setMood("loading");
      if (localStorage.getItem("user")) {
        let response;
        try {
          response = await controller.getOnboardingSteps();
        } catch (e) {
          // Backend unreachable — show a retry screen, not a blank/forever spinner.
          setMood("connectionError");
          return;
        }
        if (!response || !response.result) {
          setMood("connectionError");
          return;
        }

        const onboarding_name = response.result.length === 0 ? "completed" : response.result[0].onboarding_step_name;
        const practiceId = response.result.length === 0 ? null : response.result[0].organizations[0];
        const officeId = response.result.length === 0 ? null : response.result[0].branches[0];
        // Only write a real id. setItem coerces null/undefined to the literal
        // strings "null"/"undefined", which then travelled into requests as an
        // office id (the trial button POSTed "undefined"). A step with no branch
        // tag keeps whatever the Purchase step's office lookup already stored.
        if (officeId != null) {
          localStorage.setItem("selectedOffice", officeId);
        }

        if (practiceId) setPracticeId(practiceId);
        if (officeId) setOfficeId(officeId);
        if (onboarding_name === "signed_up") {
          setMood("multipleOffice");
        } else if (onboarding_name === "create_office_practice_start") {
          setMood("createOfficePractice");
        } else if (onboarding_name === "office_practice_created") {
          setMood("UpdateOfficePractice");
        } else if (onboarding_name === "create_practice_start") {
          setMood("createPractice");
        } else if (onboarding_name === "practice_created") {
          setMood("UpdatePractice");
        } else if (onboarding_name === "practice_updated") {
          setMood("CreateOffice");
        } else if (onboarding_name === "office_created") {
          setMood("UpdateOffice");
        } else if (onboarding_name === "office_updated" || onboarding_name === "office_practice_updated") {
          setMood("configureBanking");
        } else if (onboarding_name === "connected_account_created") {
          setMood("enableBank");
        } else if (onboarding_name === "connected_account_enabled") {
          // Fetched before the screen shows, so its Next knows where to go.
          setPlan(await controller.getOnboardingPlan());
          setMood("displayBankAccount");
        } else if (onboarding_name === "invite_users") {
          // Fetched before the screen shows, so the full invite list never flashes
          // for a registrant who only gets the "another office?" question.
          setPlan(await controller.getOnboardingPlan());
          setMood("InviteUsers");
        } else if (onboarding_name === "invite_users_skipped") {
          // test
          setMood("AddNewOffice");
        } else if (onboarding_name === "users_invited") {
          setMood("Purchase");
        } else if (onboarding_name === "sikka_setup") {
          setMood("SikkaSetup");
        } else if (onboarding_name === "portal_credentials") {
          setMood("PortalCredentials");
        } else if (onboarding_name === "procedure_code_defaults") {
          setMood("ProcedureCodeDefaults");
        } else if (onboarding_name === "completed") {
          // Onboarding finished — persist the flag and hand off to the router,
          // which renders the dashboard (there is no "Done" screen here). Show a
          // spinner instead of the bare "-" fallback during the brief reload.
          localStorage.setItem("onBoardingStep", "completed");
          setMood("completed");
          window.location.reload();
        }
      } else {
        setMood("login");
      }
    }
  };

  useEffect(() => {
    console.log(mood);
  }, [mood]);

  useEffect(() => {
    readOnboardingStatus();
  }, []);

  // The bank-connected screen's Next. Usually the invite screen; for a
  // single-office practice registered without the invite step, move straight on
  // (the backend picks the next screen) and re-read where they are.
  const ChangeModeToInvite = async () => {
    if (afterBankAction(plan) === "skip") {
      setMood("loading");
      try {
        await controller.skipOnBoarding();
      } catch (e) {
        // Fall through: re-reading still lands them somewhere real.
      }
      readOnboardingStatus();
      return;
    }
    setMood("InviteUsers");
  };

  const goToForgetPass = () => {
    setMood("forgetPass");
  };

  return (
    <>
      <LoginLayout variant={mood === "Purchase" || mood === "PortalCredentials" || mood === "ProcedureCodeDefaults" ? "wide" : undefined}>
        {mood === "login" ? (
          <Login goToForgetPass={goToForgetPass} goToSignUpPage={goToSignUpPage} readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "signUp" ? (
          <SignUp goToLoginPage={goToLoginPage} />
        ) : mood === "multipleOffice" ? (
          <MultipleOffice readOnboardingStatus={readOnboardingStatus} practiceId={practiceId} />
        ) : mood === "createOfficePractice" ? (
          <CreateOfficePractice readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "createPractice" ? (
          <CreatePractice readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "UpdatePractice" ? (
          <UpdatePractice readOnboardingStatus={readOnboardingStatus} practiceId={practiceId} />
        ) : mood === "UpdateOfficePractice" ? (
          <UpdateOfficePractice readOnboardingStatus={readOnboardingStatus} practiceId={practiceId} />
        ) : mood === "CreateOffice" ? (
          <CreateOffice readOnboardingStatus={readOnboardingStatus} practiceId={practiceId} />
        ) : mood === "UpdateOffice" ? (
          <UpdateOffice officeId={officeId} readOnboardingStatus={readOnboardingStatus} practiceId={practiceId} />
        ) : mood === "configureBanking" ? (
          <CreateBankAccount officeId={officeId} readOnboardingStatus={readOnboardingStatus} practiceId={practiceId} />
        ) : mood === "enableBank" ? (
          <EnableBank readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "displayBankAccount" ? (
          <DisplayBankAccount ChangeModeToInvite={ChangeModeToInvite} />
        ) : mood === "InviteUsers" ? (
          <InviteUsers readOnboardingStatus={readOnboardingStatus} plan={plan} />
        ) : mood === "AddNewOffice" ? (
          <AddNewOffice readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "Purchase" ? (
          <Purchase readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "SikkaSetup" ? (
          <SikkaSetup officeId={officeId} practiceId={practiceId} readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "PortalCredentials" ? (
          <PortalCredentials readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "ProcedureCodeDefaults" ? (
          <ProcedureCodeDefaults readOnboardingStatus={readOnboardingStatus} />
        ) : mood === "forgetPass" ? (
          <ForgetPass goToLoginPage={goToLoginPage} readOnboardingStatus={readOnboardingStatus} goToSignUpPage={goToSignUpPage} />
        ) : mood === "loading" ? (
          <Spin />
        ) : mood === "completed" ? (
          <Spin />
        ) : mood === "connectionError" ? (
          <div style={{ maxWidth: 460 }}>
            <p style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Can't reach the server</p>
            <p style={{ color: "#555", marginBottom: 16 }}>
              We couldn't load your onboarding. Check that the app is online, then try again.
            </p>
            <Button onClick={readOnboardingStatus} className="login-button">
              Retry
            </Button>
          </div>
        ) : (
          <div>-</div>
        )}
      </LoginLayout>
    </>
  );
};

export default LoginPage;
